import { useEffect, useRef, useState } from 'react'
import { View, Text, TextInput as RNTextInput } from 'react-native'
import { useTranslation } from 'react-i18next'
import { FONTS, SPACING, RADIUS } from '@/lib/brand'
import { useSemanticColors } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import { Button } from '@/components/ui/Button'
import { TextInput } from '@/components/ui/TextInput'
import { handoverPresenceService } from '@/lib/services/handover-presence.service'
import { notification, NotificationType } from '@/lib/utils/haptics'
import { toast } from '@/lib/toast'
import { captureError } from '@/lib/utils/observability'
import { productTelemetryService } from '@/lib/services/product-telemetry.service'
import { RETENTION_EVENTS } from '@/lib/retention/events'
import { normalizeHandoverPresenceCode } from '@/lib/utils/handover-presence-code'
import { HandoverQr } from './HandoverQr'
import { HandoverScanModal } from './HandoverScanModal'

interface HandoverPresenceCardProps {
  rentalId: string
  inspectionType: 'pickup' | 'dropoff'
  /** true = quien ve esta pantalla es el huésped; false = el anfitrión. */
  isGuest: boolean
}

/**
 * La prueba de presencia del traspaso: un código efímero que muestra un
 * teléfono y verifica el otro. Evidencia, jamás compuerta — un traspaso
 * off-app sigue siendo válido, esto solo lo documenta.
 */
export function HandoverPresenceCard({
  rentalId,
  inspectionType,
  isGuest,
}: HandoverPresenceCardProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  const [code, setCode] = useState<string | null>(null)
  const [expiresAt, setExpiresAt] = useState<number | null>(null)
  const [remaining, setRemaining] = useState<number | null>(null)
  const [entered, setEntered] = useState('')
  const [verified, setVerified] = useState(false)
  const [busy, setBusy] = useState(false)
  const [scanOpen, setScanOpen] = useState(false)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const codeInputRef = useRef<RNTextInput>(null)

  useEffect(() => {
    let active = true
    handoverPresenceService
      .getPresenceStatus(rentalId, inspectionType)
      .then((status) => {
        if (active && status) setVerified(true)
      })
      .catch((err) => {
        captureError(err, { screen: 'handover-presence', action: 'status' })
      })
    return () => {
      active = false
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [rentalId, inspectionType])

  useEffect(() => {
    if (!expiresAt) return
    const tick = () => {
      const left = expiresAt - Date.now()
      setRemaining(left > 0 ? left : 0)
      if (left <= 0) {
        if (timerRef.current) clearInterval(timerRef.current)
        setCode(null)
      }
    }
    tick()
    timerRef.current = setInterval(tick, 1000)
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [expiresAt])

  const mint = async () => {
    setBusy(true)
    try {
      const token = await handoverPresenceService.mintToken(rentalId, inspectionType)
      setCode(token.code)
      setExpiresAt(Date.parse(token.expiresAt))
      productTelemetryService.recordEvent('booking_step', RETENTION_EVENTS.handoverQrShown, {
        elementId: 'handover-presence.qr',
      })
      void notification(NotificationType.Success)
    } catch (err) {
      captureError(err, { screen: 'handover-presence', action: 'mint' })
      toast.error(t('handover_presence.mint_failed'))
    } finally {
      setBusy(false)
    }
  }

  const verify = async (raw?: string) => {
    const value = (raw ?? entered)
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 6)
    if (value.length !== 6 || busy) return
    setBusy(true)
    try {
      await handoverPresenceService.verifyCode(value, rentalId, inspectionType)
      setVerified(true)
      setEntered('')
      productTelemetryService.recordEvent('booking_step', RETENTION_EVENTS.handoverVerified, {
        elementId: 'handover-presence.verified',
      })
      void notification(NotificationType.Success)
    } catch (err) {
      captureError(err, { screen: 'handover-presence', action: 'verify' })
      toast.error(t('handover_presence.verify_failed'))
    } finally {
      setBusy(false)
    }
  }

  if (verified) {
    return (
      <View
        testID="handover-presence.verified"
        accessibilityRole="text"
        accessibilityLabel={t('handover_presence.verified_a11y')}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: SPACING.sm,
          padding: SPACING.md,
          borderRadius: RADIUS.lg,
          borderWidth: 1,
          borderColor: colors.border,
          backgroundColor: colors.surface,
        }}
      >
        <Text
          style={{
            fontSize: 18,
            lineHeight: 24,
            fontFamily: FONTS.bodyBold,
            color: colors.textPrimary,
          }}
        >
          ✓
        </Text>
        <Text
          style={{
            flex: 1,
            fontSize: 14,
            lineHeight: 20,
            fontFamily: FONTS.body,
            color: colors.textSecondary,
          }}
        >
          {t('handover_presence.verified')}
        </Text>
      </View>
    )
  }

  const minutes = remaining != null ? Math.floor(remaining / 60_000) : 5
  const seconds = remaining != null ? Math.floor((remaining % 60_000) / 1000) : 0

  return (
    <View
      testID="handover-presence"
      style={{
        padding: SPACING.md,
        borderRadius: RADIUS.lg,
        borderWidth: 1,
        borderColor: colors.border,
        backgroundColor: colors.surface,
        gap: SPACING.sm,
      }}
    >
      <Text
        style={{
          fontSize: 12,
          fontFamily: FONTS.body,
          color: colors.textMuted,
          textTransform: 'uppercase',
          letterSpacing: 0.6,
        }}
      >
        {t('handover_presence.kicker')}
      </Text>

      {isGuest ? (
        <>
          <TextInput
            label={t('handover_presence.guest_label')}
            placeholder={t('handover_presence.guest_placeholder')}
            value={entered}
            ref={codeInputRef}
            onChangeText={(value) =>
              setEntered(normalizeHandoverPresenceCode(value, codeInputRef.current))
            }
            autoCapitalize="characters"
            autoCorrect={false}
            autoComplete="off"
            textContentType="none"
            returnKeyType="done"
            onSubmitEditing={() => void verify()}
            testID="handover-presence.code-input"
          />
          <Button
            title={t('handover_presence.guest_cta')}
            onPress={() => void verify()}
            loading={busy}
            disabled={entered.length !== 6 || busy}
            testID="handover-presence.verify-button"
          />
          <Button
            title={t('handover_presence.scan_cta')}
            variant="secondary"
            onPress={() => setScanOpen(true)}
            disabled={busy}
            testID="handover-presence.scan-button"
          />
          <HandoverScanModal
            visible={scanOpen}
            onClose={() => setScanOpen(false)}
            onCode={(scanned) => {
              setEntered(scanned)
              void verify(scanned)
            }}
          />
        </>
      ) : code ? (
        <>
          <HandoverQr payload={`tago-h/${code}`} />
          <Text
            testID="handover-presence.code"
            accessibilityLabel={t('handover_presence.code_a11y', { code })}
            style={{
              fontSize: 34,
              lineHeight: 42,
              fontFamily: FONTS.mono,
              letterSpacing: 10,
              color: colors.textPrimary,
              textAlign: 'center',
            }}
          >
            {code}
          </Text>
          <Text
            style={{
              fontSize: 13,
              lineHeight: 18,
              fontFamily: FONTS.body,
              color: colors.textSecondary,
              textAlign: 'center',
            }}
          >
            {t('handover_presence.expires', { minutes, seconds: String(seconds).padStart(2, '0') })}
          </Text>
          <Button
            title={t('handover_presence.mint_again')}
            variant="secondary"
            onPress={() => void mint()}
            loading={busy}
            testID="handover-presence.remint-button"
          />
        </>
      ) : (
        <>
          <Text
            style={{
              fontSize: 14,
              lineHeight: 20,
              fontFamily: FONTS.body,
              color: colors.textSecondary,
            }}
          >
            {t('handover_presence.host_hint')}
          </Text>
          <Button
            title={t('handover_presence.mint_cta')}
            onPress={() => void mint()}
            loading={busy}
            testID="handover-presence.mint-button"
          />
        </>
      )}
    </View>
  )
}
